import React, { useId } from 'react';

/**
 * The ARC product mark: two open arcs orbiting a core. Ported from
 * fenxchat-marketing's `ArcMark.tsx` (the canonical implementation) so the
 * portal stops using the retired three-chevron glyph that was still sitting
 * in the copied `arc-icons.svg` sprite (`#i-arc-mark`) - that sprite file
 * predates the 26 August 2026 chevron -> orbit-mark swap and was never
 * updated in the source brand package. Do not reintroduce the chevrons.
 *
 * Geometry is transcribed from the approved kit
 * (`logos/arc/svg/arc-gradient.svg`), not redrawn - see fenxchat-marketing's
 * ArcMark.tsx for the full derivation of the optical nudge, the dash-offset
 * math, and the single-sweep gradient. Keep this file's geometry identical
 * to that one if either ever changes.
 */

export type ArcMarkVariant = 'gradient' | 'ink' | 'white' | 'current';

export interface ArcMarkProps {
  variant?: ArcMarkVariant;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

const solidFills: Record<Exclude<ArcMarkVariant, 'gradient'>, string> = {
  ink: '#1C1618',
  white: '#FFFFFF',
  current: 'currentColor',
};

const INK_LO = 7.5;
const INK_HI = 92.5;
const INK_SPAN = INK_HI - INK_LO;

function ArcGradient({ id }: { id: string }) {
  return (
    <linearGradient
      id={id}
      gradientUnits="userSpaceOnUse"
      x1={INK_LO + 1 * INK_SPAN}
      y1={INK_LO + 0.05 * INK_SPAN}
      x2={INK_LO + 0.05 * INK_SPAN}
      y2={INK_LO + 1 * INK_SPAN}
    >
      <stop offset="0" stopColor="#FFD21E" />
      <stop offset="0.26" stopColor="#F7A23C" />
      <stop offset="0.48" stopColor="#EE7F60" />
      <stop offset="0.74" stopColor="#D6206E" />
      <stop offset="1" stopColor="#5E1A55" />
    </linearGradient>
  );
}

function ArcGeometry({ paint }: { paint: string }) {
  return (
    <g transform="translate(0 4.5)">
      <circle cx="50" cy="50" r="38" strokeWidth="9" strokeDasharray="179 60" strokeDashoffset="146" />
      <circle cx="50" cy="50" r="21" strokeWidth="7" strokeDasharray="66 66" strokeDashoffset="14.8" />
      <circle cx="50" cy="50" r="7.5" fill={paint} stroke="none" />
    </g>
  );
}

export function ArcMark({ variant = 'gradient', size = 24, className, style, title }: ArcMarkProps) {
  const reactId = useId();
  const gradientId = `arc-grad-${reactId.replace(/:/g, '')}`;
  const paint = variant === 'gradient' ? `url(#${gradientId})` : solidFills[variant];

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={{ flexShrink: 0, ...style }}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {variant === 'gradient' && (
        <defs>
          <ArcGradient id={gradientId} />
        </defs>
      )}
      <g fill="none" stroke={paint} strokeLinecap="round">
        <ArcGeometry paint={paint} />
      </g>
    </svg>
  );
}
